import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Cropper from 'react-easy-crop';

import { API } from 'common';
import { toastRef } from 'components/Toast';
import { CropAreaPixels, cropAvatarImage } from 'utils/crop-avatar-image';

interface AvatarEditorModalProps {
	isOpen: boolean;
	/** Staff whose avatar is replaced (self, or any staff when an Admin edits from Staff List). */
	staffId: number | string;
	/** Logged-in staff id from the auth cookie; BE enforces self-or-Admin. */
	requesterStaffId: number | string;
	onClose: () => void;
	/** Fired with the new S3 URL after a successful save, so callers can patch their state. */
	onUpdated: (urlImage: string) => void;
}

/**
 * Pick a photo -> auto-centered square crop (drag/zoom adjustable) -> 300x300
 * JPEG -> PUT staff/update-avatar. Shared by the Account page (self) and the
 * manager Staff List (any staff of the shop).
 */
const AvatarEditorModal = ({ isOpen, staffId, requesterStaffId, onClose, onUpdated }: AvatarEditorModalProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [imageSrc, setImageSrc] = useState<string | null>(null);
	// react-easy-crop starts centered at zoom 1 => the default is the centered max square.
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [areaPixels, setAreaPixels] = useState<CropAreaPixels | null>(null);
	const [saving, setSaving] = useState(false);

	// Release the previous object URL whenever it is replaced or the modal unmounts.
	useEffect(() => {
		return () => {
			if (imageSrc) URL.revokeObjectURL(imageSrc);
		};
	}, [imageSrc]);

	const reset = () => {
		setImageSrc(null);
		setCrop({ x: 0, y: 0 });
		setZoom(1);
		setAreaPixels(null);
		setSaving(false);
	};

	const handleClose = () => {
		if (saving) return;
		reset();
		onClose();
	};

	const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files && e.target.files[0];
		// Allow re-picking the same file after an error.
		e.target.value = '';
		if (!file) return;
		setCrop({ x: 0, y: 0 });
		setZoom(1);
		setAreaPixels(null);
		setImageSrc(URL.createObjectURL(file));
	};

	const onCropComplete = useCallback((_croppedArea: unknown, croppedAreaPixels: CropAreaPixels) => {
		setAreaPixels(croppedAreaPixels);
	}, []);

	const onCropperError = () => {
		// e.g. HEIC on desktop Chrome: the browser cannot decode it.
		setImageSrc(null);
		if (toastRef.current) toastRef.current.show('Unsupported image format');
	};

	const onSave = async () => {
		if (!imageSrc || !areaPixels || saving) return;
		setSaving(true);
		try {
			const imageBase64 = await cropAvatarImage(imageSrc, areaPixels);
			const response = await API.put('staff/update-avatar', {
				payload: {
					staffId,
					requesterStaffId,
					imageBase64,
				},
			});
			const urlImage = response.data?.payload?.urlImage;
			if (toastRef.current) toastRef.current.success('Avatar updated');
			if (urlImage) onUpdated(urlImage);
			reset();
			onClose();
		} catch (error: any) {
			// 403 carries both causes (not your avatar / inactive account) -> show the BE message.
			if (toastRef.current) toastRef.current.show(error?.response?.data?.message || 'Failed to update avatar!');
			setSaving(false);
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={handleClose} centered className="avatar-editor-modal">
			<ModalHeader toggle={handleClose}>Change photo</ModalHeader>
			<ModalBody>
				{imageSrc ? (
					<>
						<div className="avatar-editor-crop-area">
							<Cropper
								image={imageSrc}
								crop={crop}
								zoom={zoom}
								minZoom={1}
								maxZoom={3}
								aspect={1}
								cropShape="round"
								showGrid={false}
								onCropChange={setCrop}
								onZoomChange={setZoom}
								onCropComplete={onCropComplete}
								mediaProps={{ onError: onCropperError }}
							/>
						</div>
						<input
							type="range"
							className="avatar-editor-zoom"
							min={1}
							max={3}
							step={0.05}
							value={zoom}
							aria-label="Zoom"
							onChange={e => setZoom(Number(e.target.value))}
						/>
					</>
				) : (
					<button type="button" className="avatar-editor-pick" onClick={() => inputRef.current?.click()}>
						Choose a photo
					</button>
				)}
				<input
					ref={inputRef}
					type="file"
					accept="image/*"
					style={{ display: 'none' }}
					onChange={onPickFile}
				/>
			</ModalBody>
			<ModalFooter>
				{imageSrc && (
					<Button color="secondary" outline disabled={saving} onClick={() => inputRef.current?.click()}>
						Pick another
					</Button>
				)}
				<Button color="primary" disabled={!imageSrc || !areaPixels || saving} onClick={onSave}>
					{saving ? 'Saving...' : 'Save'}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default AvatarEditorModal;
